/**
 * RolesAdmin Page
 * Admin interface for managing roles and permissions with drag & drop editor
 */
import { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { roles as rolesApi } from '../services/api';
import {
    Shield, Plus, Edit2, Trash2, Check, X, Loader2,
    ChevronRight, ChevronDown, Lock, Users, Save,
    GripVertical, Eye, FilePlus, Pencil, Trash
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Helper to get Lucide icon by name
const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent || LucideIcons.Circle;
};

// Sortable menu item component
const SortableMenuItem = ({ item, onRemove, onTogglePermission, permissions, isSystemRole }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const Icon = getIcon(item.icono);
    const itemPermissions = permissions.filter(p => p.menu_item_id === item.id);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="bg-white/5 rounded-xl p-3 border border-white/10 hover:border-cyan-500/30 transition-all"
        >
            <div className="flex items-center gap-3">
                <button
                    className="cursor-grab hover:text-cyan-400 text-gray-500"
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="w-4 h-4" />
                </button>
                <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="flex-1">
                    <p className="text-white font-medium text-sm">{item.nombre}</p>
                    <p className="text-gray-500 text-xs">{item.ruta || 'Sin ruta'}</p>
                </div>
                {/* Ocultar botón de eliminar en roles de sistema */}
                {!isSystemRole && (
                    <button
                        onClick={() => onRemove(item.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                        title="Quitar del rol"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Permission toggles */}
            {item.ruta && (
                <div className="flex gap-2 mt-2 ml-11">
                    {['ver', 'crear', 'editar', 'borrar'].map(accion => {
                        const perm = itemPermissions.find(p => p.accion === accion);
                        const isActive = perm?.active;
                        const icons = { ver: Eye, crear: FilePlus, editar: Pencil, borrar: Trash };
                        const ActionIcon = icons[accion];

                        return (
                            <button
                                key={accion}
                                onClick={() => perm && onTogglePermission(perm.id)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-all ${isActive
                                    ? 'bg-cyan-500/30 text-cyan-300'
                                    : 'bg-white/5 text-gray-500 hover:bg-white/10'
                                    }`}
                                title={accion}
                            >
                                <ActionIcon className="w-3 h-3" />
                                {accion}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Available menu item (draggable source)
const AvailableMenuItem = ({ item, onAdd, isAdded }) => {
    const Icon = getIcon(item.icono);

    return (
        <div
            className={`p-3 rounded-xl border transition-all ${isAdded
                ? 'bg-gray-800/50 border-gray-700 opacity-50 cursor-not-allowed'
                : 'bg-white/5 border-white/10 hover:border-cyan-500/50 cursor-pointer hover:bg-white/10'
                }`}
            onClick={() => !isAdded && onAdd(item)}
        >
            <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{item.nombre}</p>
                    <p className="text-gray-500 text-xs truncate">{item.ruta || 'Categoría'}</p>
                </div>
                {isAdded ? (
                    <Check className="w-4 h-4 text-green-400" />
                ) : (
                    <Plus className="w-4 h-4 text-gray-400" />
                )}
            </div>
        </div>
    );
};

const RolesAdmin = () => {
    const toast = useToast();
    const [roles, setRoles] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [allPermisos, setAllPermisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleMenus, setRoleMenus] = useState([]);
    const [rolePermissions, setRolePermissions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Modal states
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [formData, setFormData] = useState({ nombre: '', codigo: '', descripcion: '' });
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, role: null });
    const [unsavedChangesConfirm, setUnsavedChangesConfirm] = useState({ isOpen: false, nextRole: null });

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [rolesData, menusData, permisosData] = await Promise.all([
                rolesApi.getAll(),
                rolesApi.getMenuFlat(),
                rolesApi.getPermisos()
            ]);
            setRoles(Array.isArray(rolesData) ? rolesData : []);
            setMenuItems(Array.isArray(menusData) ? menusData : []);
            setAllPermisos(Array.isArray(permisosData) ? permisosData : []);
        } catch (error) {
            toast.error('Error al cargar datos');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const loadRoleData = async (role) => {
        try {
            const roleData = await rolesApi.get(role.id);
            setSelectedRole(roleData);

            // Build the menus list from permissions
            const menuIds = [...new Set(roleData.permisos.map(p => p.menu_item_id))];
            const menus = menuIds.map(id => {
                const perm = roleData.permisos.find(p => p.menu_item_id === id);
                return menuItems.find(m => m.id === id) || { id, nombre: perm.menu_nombre };
            });
            setRoleMenus(menus);

            // Build permissions with active state
            const perms = allPermisos.map(p => ({
                ...p,
                active: roleData.permisos.some(rp => rp.id === p.id)
            }));
            setRolePermissions(perms);
            setHasChanges(false);
        } catch (error) {
            toast.error('Error al cargar rol');
        }
    };

    const selectRole = async (role) => {
        if (hasChanges) {
            setUnsavedChangesConfirm({ isOpen: true, nextRole: role });
            return;
        }

        await loadRoleData(role);
    };

    const confirmDiscardChanges = () => {
        if (!unsavedChangesConfirm.nextRole) return;
        loadRoleData(unsavedChangesConfirm.nextRole);
    };

    const handleAddMenu = (menu) => {
        if (roleMenus.some(m => m.id === menu.id)) return;

        setRoleMenus([...roleMenus, menu]);
        // Activate 'ver' permission by default
        setRolePermissions(prev => prev.map(p =>
            p.menu_item_id === menu.id && p.accion === 'ver'
                ? { ...p, active: true }
                : p
        ));
        setHasChanges(true);
    };

    const handleRemoveMenu = (menuId) => {
        setRoleMenus(roleMenus.filter(m => m.id !== menuId));
        // Deactivate all permissions for this menu
        setRolePermissions(prev => prev.map(p =>
            p.menu_item_id === menuId ? { ...p, active: false } : p
        ));
        setHasChanges(true);
    };

    const handleTogglePermission = (permId) => {
        setRolePermissions(prev => prev.map(p =>
            p.id === permId ? { ...p, active: !p.active } : p
        ));
        setHasChanges(true);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setRoleMenus((items) => {
                const oldIndex = items.findIndex(i => i.id === active.id);
                const newIndex = items.findIndex(i => i.id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
            setHasChanges(true);
        }
    };

    const savePermissions = async () => {
        if (!selectedRole) return;

        try {
            setSaving(true);
            const activePermisos = rolePermissions
                .filter(p => p.active)
                .map((p, idx) => ({
                    permiso_id: p.id,
                    orden_menu: idx
                }));

            await rolesApi.updatePermisos(selectedRole.id, activePermisos);
            toast.success('Permisos guardados correctamente');
            setHasChanges(false);
            loadData();
        } catch (error) {
            toast.error(error.message || 'Error al guardar permisos');
        } finally {
            setSaving(false);
        }
    };

    // Role CRUD
    const openCreateRoleModal = () => {
        setEditingRole(null);
        setFormData({ nombre: '', codigo: '', descripcion: '' });
        setShowRoleModal(true);
    };

    const openEditRoleModal = (role) => {
        setEditingRole(role);
        setFormData({
            nombre: role.nombre,
            codigo: role.codigo,
            descripcion: role.descripcion || ''
        });
        setShowRoleModal(true);
    };

    const handleRoleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim() || !formData.codigo.trim()) {
            toast.error('Nombre y código son requeridos');
            return;
        }

        try {
            setSaving(true);
            if (editingRole) {
                await rolesApi.update(editingRole.id, formData);
                toast.success('Rol actualizado');
            } else {
                await rolesApi.create(formData);
                toast.success('Rol creado');
            }
            setShowRoleModal(false);
            loadData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRole = async () => {
        const role = deleteConfirm.role;
        if (!role) return;

        try {
            await rolesApi.delete(role.id);
            toast.success('Rol eliminado');
            if (selectedRole?.id === role.id) {
                setSelectedRole(null);
                setRoleMenus([]);
            }
            loadData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Shield className="w-7 h-7 text-purple-400" />
                        Roles y Permisos
                    </h1>
                    <p className="text-gray-400 mt-1">
                        Gestiona los roles del sistema y asigna permisos arrastrando menús
                    </p>
                </div>
                <button onClick={openCreateRoleModal} className="btn-primary flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Nuevo Rol
                </button>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Roles List */}
                <div className="col-span-3">
                    <div className="card">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <Users className="w-5 h-5 text-cyan-400" />
                            Roles
                        </h3>
                        <div className="space-y-2">
                            {roles.map(role => (
                                <button
                                    key={role.id}
                                    onClick={() => selectRole(role)}
                                    className={`w-full p-3 rounded-xl text-left transition-all ${selectedRole?.id === role.id
                                        ? 'bg-cyan-500/20 border border-cyan-500/50'
                                        : 'bg-white/5 border border-transparent hover:bg-white/10'
                                        }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            {role.es_sistema && <Lock className="w-3 h-3 text-amber-400" />}
                                            <span className="font-medium text-white">{role.nombre}</span>
                                        </div>
                                        <span className="text-xs text-gray-500">{role.usuarios_count || 0}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{role.codigo}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Editor */}
                <div className="col-span-9">
                    {selectedRole ? (
                        <div className="grid grid-cols-2 gap-6">
                            {/* Available Menus */}
                            <div className="card">
                                <h3 className="text-lg font-semibold text-white mb-4">
                                    Menús Disponibles
                                </h3>
                                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                                    {menuItems.filter(m => m.ruta).map(menu => (
                                        <AvailableMenuItem
                                            key={menu.id}
                                            item={menu}
                                            onAdd={handleAddMenu}
                                            isAdded={roleMenus.some(m => m.id === menu.id)}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Role Menus */}
                            <div className="card">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-white">
                                        Menú de {selectedRole.nombre}
                                        {selectedRole.es_sistema && (
                                            <span className="ml-2 text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
                                                Sistema
                                            </span>
                                        )}
                                    </h3>
                                    {!selectedRole.es_sistema && (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openEditRoleModal(selectedRole)}
                                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400"
                                                title="Editar rol"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteConfirm({ isOpen: true, role: selectedRole })}
                                                className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400"
                                                title="Eliminar rol"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {selectedRole.es_sistema && (
                                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 text-cyan-300 text-sm mb-4">
                                        <Lock className="w-4 h-4 inline mr-2" />
                                        Rol de sistema: puedes <strong>añadir</strong> permisos, pero no quitar los existentes.
                                    </div>
                                )}

                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={roleMenus.map(m => m.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                                            {roleMenus.length === 0 ? (
                                                <div className="text-center py-8 text-gray-500">
                                                    <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                                    <p>Arrastra menús aquí para asignar permisos</p>
                                                </div>
                                            ) : (
                                                roleMenus.map(menu => (
                                                    <SortableMenuItem
                                                        key={menu.id}
                                                        item={menu}
                                                        onRemove={handleRemoveMenu}
                                                        onTogglePermission={handleTogglePermission}
                                                        permissions={rolePermissions.filter(p => p.menu_item_id === menu.id)}
                                                        isSystemRole={selectedRole.es_sistema}
                                                    />
                                                ))
                                            )}
                                        </div>
                                    </SortableContext>
                                </DndContext>

                                {/* Save button */}
                                {hasChanges && (
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                        <button
                                            onClick={savePermissions}
                                            disabled={saving}
                                            className="btn-primary w-full flex items-center justify-center gap-2"
                                        >
                                            {saving ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Guardando...
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="w-4 h-4" />
                                                    Guardar Cambios
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="card text-center py-16">
                            <Shield className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                            <h3 className="text-xl font-semibold text-gray-400 mb-2">
                                Selecciona un rol
                            </h3>
                            <p className="text-gray-500">
                                Elige un rol de la lista para editar sus permisos
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Role Modal */}
            {showRoleModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/70">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md">
                        <div className="p-6 border-b border-white/10">
                            <h2 className="text-xl font-bold text-white">
                                {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
                            </h2>
                        </div>
                        <form onSubmit={handleRoleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Nombre *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej: Mecánico"
                                    className="input-field"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Código *
                                </label>
                                <input
                                    type="text"
                                    value={formData.codigo}
                                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                                    placeholder="Ej: mecanico"
                                    className="input-field"
                                    disabled={editingRole}
                                />
                                <p className="text-xs text-gray-500 mt-1">Identificador único, sin espacios</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    placeholder="Descripción del rol..."
                                    rows={3}
                                    className="input-field resize-none"
                                />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowRoleModal(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4" />
                                            {editingRole ? 'Guardar' : 'Crear'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            <ConfirmModal
                isOpen={unsavedChangesConfirm.isOpen}
                onClose={() => setUnsavedChangesConfirm({ isOpen: false, nextRole: null })}
                onConfirm={confirmDiscardChanges}
                title="Descartar cambios sin guardar"
                message="Tienes cambios sin guardar en este rol. ¿Quieres descartarlos y cambiar de rol?"
                confirmText="Descartar y continuar"
                variant="warning"
            />

            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, role: null })}
                onConfirm={handleDeleteRole}
                title="Eliminar Rol"
                message={`¿Estás seguro de que deseas eliminar el rol "${deleteConfirm.role?.nombre}"?`}
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
};

export default RolesAdmin;
